import { useMemo } from 'react';
import { SearchableSelect } from './combobox';

// ISO 3166-1 alpha-2 code, English name, and international calling code.
// Covers the countries Boothiva customers and their guests are realistically based in;
// extend this list as new markets are supported.
export const COUNTRIES: { code: string; name: string; dialCode: string }[] = [
  { code: 'AU', name: 'Australia', dialCode: '+61' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { code: 'US', name: 'United States', dialCode: '+1' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { code: 'IE', name: 'Ireland', dialCode: '+353' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'DE', name: 'Germany', dialCode: '+49' },
  { code: 'ES', name: 'Spain', dialCode: '+34' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'IT', name: 'Italy', dialCode: '+39' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31' },
  { code: 'BE', name: 'Belgium', dialCode: '+32' },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41' },
  { code: 'AT', name: 'Austria', dialCode: '+43' },
  { code: 'SE', name: 'Sweden', dialCode: '+46' },
  { code: 'NO', name: 'Norway', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', dialCode: '+45' },
  { code: 'FI', name: 'Finland', dialCode: '+358' },
  { code: 'IS', name: 'Iceland', dialCode: '+354' },
  { code: 'PL', name: 'Poland', dialCode: '+48' },
  { code: 'CZ', name: 'Czechia', dialCode: '+420' },
  { code: 'SK', name: 'Slovakia', dialCode: '+421' },
  { code: 'HU', name: 'Hungary', dialCode: '+36' },
  { code: 'RO', name: 'Romania', dialCode: '+40' },
  { code: 'BG', name: 'Bulgaria', dialCode: '+359' },
  { code: 'GR', name: 'Greece', dialCode: '+30' },
  { code: 'HR', name: 'Croatia', dialCode: '+385' },
  { code: 'SI', name: 'Slovenia', dialCode: '+386' },
  { code: 'EE', name: 'Estonia', dialCode: '+372' },
  { code: 'LV', name: 'Latvia', dialCode: '+371' },
  { code: 'LT', name: 'Lithuania', dialCode: '+370' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380' },
  { code: 'MT', name: 'Malta', dialCode: '+356' },
  { code: 'CY', name: 'Cyprus', dialCode: '+357' },
  { code: 'JP', name: 'Japan', dialCode: '+81' },
  { code: 'KR', name: 'South Korea', dialCode: '+82' },
  { code: 'CN', name: 'China', dialCode: '+86' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886' },
  { code: 'SG', name: 'Singapore', dialCode: '+65' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62' },
  { code: 'TH', name: 'Thailand', dialCode: '+66' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84' },
  { code: 'IN', name: 'India', dialCode: '+91' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94' },
  { code: 'NP', name: 'Nepal', dialCode: '+977' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { code: 'QA', name: 'Qatar', dialCode: '+974' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973' },
  { code: 'OM', name: 'Oman', dialCode: '+968' },
  { code: 'IL', name: 'Israel', dialCode: '+972' },
  { code: 'TR', name: 'Türkiye', dialCode: '+90' },
  { code: 'EG', name: 'Egypt', dialCode: '+20' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234' },
  { code: 'KE', name: 'Kenya', dialCode: '+254' },
  { code: 'GH', name: 'Ghana', dialCode: '+233' },
  { code: 'MA', name: 'Morocco', dialCode: '+212' },
  { code: 'MX', name: 'Mexico', dialCode: '+52' },
  { code: 'BR', name: 'Brazil', dialCode: '+55' },
  { code: 'AR', name: 'Argentina', dialCode: '+54' },
  { code: 'CL', name: 'Chile', dialCode: '+56' },
  { code: 'CO', name: 'Colombia', dialCode: '+57' },
  { code: 'PE', name: 'Peru', dialCode: '+51' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598' },
  { code: 'FJ', name: 'Fiji', dialCode: '+679' },
  { code: 'PG', name: 'Papua New Guinea', dialCode: '+675' },
  { code: 'RU', name: 'Russia', dialCode: '+7' },
].sort((a, b) => a.name.localeCompare(b.name));

export function CountrySelect({ value, onChange, error, help, label = 'Country' }: {
  value: string; onChange: (code: string) => void; error?: string; help?: string; label?: string;
}) {
  const options = COUNTRIES.map((country) => ({ value: country.code, label: country.name, sublabel: country.dialCode }));
  return <SearchableSelect label={label} value={value} onChange={onChange} options={options} placeholder="Search countries…" {...(help ? { help } : {})} {...(error ? { error } : {})} />;
}

function timeZoneCityLabel(zone: string) {
  const city = zone.split('/').pop() ?? zone;
  return city.replace(/_/g, ' ');
}

function timeZoneOffsetLabel(zone: string) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch { return ''; }
}

let cachedTimeZones: { id: string; label: string; sublabel: string }[] | undefined;

// Computing an offset label per zone via Intl.DateTimeFormat is relatively expensive across ~400
// zones, so this list is computed once per session (offsets don't change while the app is open)
// rather than on every render of TimeZoneSelect.
export function listTimeZones(): { id: string; label: string; sublabel: string }[] {
  if (cachedTimeZones) return cachedTimeZones;
  const zones: string[] = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [Intl.DateTimeFormat().resolvedOptions().timeZone];
  cachedTimeZones = zones
    .map((zone) => ({ id: zone, label: `${timeZoneCityLabel(zone)} (${zone})`, sublabel: timeZoneOffsetLabel(zone) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return cachedTimeZones;
}

export function TimeZoneSelect({ value, onChange, error, help, label = 'Time zone' }: {
  value: string; onChange: (id: string) => void; error?: string; help?: string; label?: string;
}) {
  const options = useMemo(() => listTimeZones().map((zone) => ({ value: zone.id, label: zone.label, sublabel: zone.sublabel })), []);
  return <SearchableSelect label={label} value={value} onChange={onChange} options={options} placeholder="Search time zones…" {...(help ? { help } : {})} {...(error ? { error } : {})} />;
}
